/**
 * The expirations list, in ONE component (docs/11 F-3, partial).
 *
 * Rendered by the Work lanes, by the Dashboard's "Needs attention" card, and by
 * nothing else — because two renderings of the same list is how the same lease
 * ends up described two ways. The card passes a small `limit` and hides the
 * filters; the lane shows everything.
 */

import {
  EXPIRATION_KINDS,
  EXPIRATION_KIND_LABEL,
  EXPIRATION_WINDOW_DAYS,
  type ExpirationKind,
  type ExpirationRow,
} from '@propos/shared';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { useMetricBarCollapse } from '../../app/usePreferences.js';
import {
  Chip,
  DateStamp,
  EmptyState,
  ErrorState,
  MetricBar,
  SkeletonCard,
} from '../../components/ui/index.js';

import { useExpirations, type ExpirationsParams } from './api.js';

/**
 * "in 12 days" / "14 days ago" — the arithmetic the reader should never do.
 *
 * A bare date is what the per-record pages already show; the whole reason this
 * screen exists is that comparing forty of them against today by hand is the
 * work nobody does. The absolute date stays beside it, because "in 12 days" is
 * useless when you are trying to put something in a calendar.
 */
function relativeDays(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days < 0 ? `${String(-days)} days ago` : `in ${String(days)} days`;
}

function ExpirationItem({ row }: { readonly row: ExpirationRow }): ReactNode {
  return (
    <li className="border-b border-hairline last:border-b-0">
      <Link
        to={row.href}
        className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-1 py-2.5 no-underline hover:bg-surface-hover"
      >
        <span className="flex min-w-0 grow flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-body font-medium text-brand-700">{row.title}</span>
            <span className="text-chip uppercase tracking-wide text-ink2">
              {EXPIRATION_KIND_LABEL[row.kind]}
            </span>
            <Chip status={row.standing} />
            {/* An auto-renewing term is a date to be aware of, not a deadline
                to act on — saying so is the difference between a list people
                work through and a list people learn to ignore. */}
            {row.autoRenew === true ? (
              <span className="text-chip text-ink2">Auto-renews</span>
            ) : null}
          </span>
          <span className="truncate text-meta text-ink2">
            {[
              row.propertyName,
              row.scopeLabel,
              row.vendorName === row.title ? null : row.vendorName,
              row.detail === row.propertyName ? null : row.detail,
            ]
              .filter((part) => part !== null && part !== '')
              .join(' · ')}
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span
            className={clsx(
              'block text-body font-medium',
              row.standing === 'expired' ? 'text-red-fg' : 'text-ink',
            )}
          >
            {relativeDays(row.daysRemaining)}
          </span>
          <span className="block text-meta text-ink2">
            <DateStamp value={row.expiration} />
          </span>
        </span>
      </Link>
    </li>
  );
}

/**
 * The "everything" tile's id. A sentinel rather than a kind, because `activeId`
 * is null when nothing is filtered and MetricBar needs SOMETHING to mark as
 * pressed for that state.
 */
const ALL_TILE = '__all__';

export function ExpirationList({
  params,
  emptyTitle,
  emptyBody,
  showKindFilter = false,
  kind = null,
  onKindChange,
}: {
  readonly params: ExpirationsParams;
  readonly emptyTitle: string;
  readonly emptyBody: string;
  readonly showKindFilter?: boolean;
  /**
   * The ONE kind being shown, or null for all.
   *
   * This was a multi-select set with everything selected by default, and it
   * read exactly backwards: Matt clicked a chip labelled "Certificate of
   * insurance 1" and the single lapsed certificate DISAPPEARED, because the
   * click deselected COIs and left four zero-count kinds behind. Every further
   * click subtracted another kind, so the list emptied as he explored it.
   *
   * A chip carrying a count reads as "show me those", so that is what it does
   * now. Single-select also removes the state that caused the confusion: there
   * is no way to arrive at a filter that excludes everything with rows in it.
   */
  readonly kind?: ExpirationKind | null;
  readonly onKindChange?: (next: ExpirationKind | null) => void;
}): ReactNode {
  const expirations = useExpirations(params);
  // Bar id, not screen id: the same strip appears on both /work lanes, and
  // somebody who hides it on Overdue means "I do not want this strip", not "not
  // on this tab".
  const collapse = useMetricBarCollapse('expiration-kinds');

  if (expirations.isPending) return <SkeletonCard rows={4} />;
  if (expirations.isError) {
    return (
      <ErrorState
        title="Something went wrong"
        body="The expirations did not load."
        onRetry={() => {
          void expirations.refetch();
        }}
      />
    );
  }

  const data = expirations.data;
  const total = Object.values(data.counts).reduce((sum, count) => sum + count, 0);
  /**
   * Read as PARTIAL on purpose. The contract types `counts` as complete and the
   * service always builds it from `emptyCounts()`, so the compiler is right that
   * the `?? 0` below is unreachable — today, against today's API. It is there for
   * the version skew this app already plans for (app/staleBuild.ts): an older API
   * answering a newer bundle omits a kind it does not know, and one tile reading
   * 0 is a far better answer than the whole screen in the error boundary. That is
   * not hypothetical — a fixture missing `key_date` did exactly that, and the old
   * markup hid it by rendering the absent count as blank.
   */
  const counts: Partial<Record<ExpirationKind, number>> = data.counts;

  return (
    <div className="flex flex-col gap-3">
      {showKindFilter ? (
        // ONE summary strip, from the design system (docs/04 §3, punch item
        // 2.1). This was a hand-rolled row of buttons here — correct, and the
        // second implementation of a catalogued component, which is exactly the
        // duplication v1 shipped twice over (docs/11 X-6.5).
        <MetricBar
          label="Filter by kind"
          activeId={kind ?? ALL_TILE}
          onSelect={(next) => {
            // The sentinel and "pressed the active tile" both mean "show
            // everything", so both arrive here as null for the caller.
            onKindChange?.(next === ALL_TILE || next === null ? null : (next as ExpirationKind));
          }}
          collapsed={collapse.collapsed}
          onCollapsedChange={collapse.setCollapsed}
          tiles={[
            // An explicit All, so returning to everything is one obvious click
            // rather than re-selecting whatever you switched off. `null` as the
            // active id means All is the pressed tile, which is why it carries
            // the sentinel rather than a kind.
            { id: ALL_TILE, label: 'All', count: total },
            ...EXPIRATION_KINDS.map((one) => ({
              id: one,
              label: EXPIRATION_KIND_LABEL[one],
              // The count is the WHOLE result, not the page: a filter tile
              // saying 3 above a list of 3 when there are 40 is worse than no
              // number.
              //
              // `?? 0` for a value the contract types as always present. It is
              // there for the version skew this app already plans for
              // (app/staleBuild.ts): an older API answering a newer bundle can
              // omit a kind it does not know, and a whole screen in the error
              // boundary is a far worse answer than one tile reading 0.
              count: counts[one] ?? 0,
              hint: ` — warned ${String(EXPIRATION_WINDOW_DAYS[one])} days ahead`,
            })),
          ]}
        />
      ) : null}

      {/* The server falls back to the whole portfolio for a caller who holds no
          property assignments. Saying so is not a detail: a list headed "my
          properties" that is quietly the whole portfolio is a lie about
          coverage, and an administrator who holds no assignments by design
          would otherwise wonder why their filter does nothing. */}
      {params.scope === 'mine' && data.scopeApplied === 'all' ? (
        <p className="text-meta text-ink2">
          You hold no property assignments, so this shows the whole portfolio.
        </p>
      ) : null}

      {data.rows.length === 0 ? (
        /* A filtered empty list needs to name the filter. "Nothing coming due"
           under a chip reading "Lease 0" invites the reading that the whole
           screen is broken, which is the reading Matt arrived at. */
        kind === null ? (
          <EmptyState title={emptyTitle} body={emptyBody} />
        ) : (
          <EmptyState
            title={`No ${EXPIRATION_KIND_LABEL[kind].toLowerCase()} in this lane`}
            body={`Nothing of this kind is ${
              params.lane === 'expired' ? 'lapsed' : 'inside its renewal window'
            }. Choose All to see the rest.`}
          />
        )
      ) : (
        <>
          <ul className="flex flex-col">
            {data.rows.map((row) => (
              <ExpirationItem key={row.key} row={row} />
            ))}
          </ul>
          {/* Never imply completeness we do not have. */}
          {data.truncated ? (
            <p className="text-meta text-ink2">
              Showing the {data.rows.length} nearest of {data.expiredCount + data.expiringCount}.
              Narrow by kind to see the rest.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
